import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Chart } from 'react-google-charts';
import { BsFileEarmarkPdf, BsFileEarmarkSpreadsheet, BsTrophyFill } from 'react-icons/bs';

import useStatistics from '../../api/statistics';
import { useCurrentUser } from '../../contexts/CurrentUser.context';
import { useCurrentPeriod } from '../../contexts/CurrentPeriod.context';
import { isWalletAdmin } from '../../helpers/auth';
import { formatMoney, formatNumber, formatPeriod } from '../../helpers/period';
import { buildSeries, cashflowLines } from '../../helpers/statistics';
import { downloadStatisticsCsv } from '../../helpers/statisticsCsv';
import { downloadStatisticsPdf } from '../../helpers/statisticsPdf';
import Loader from '../loader/Loader';
import Error from '../errors/Error';
import Forbidden from '../errors/Forbidden';
import PeriodPicker from './PeriodPicker';

const toneClass = {
  in: 'text-success',
  out: 'text-danger',
  neutral: 'text-primary',
};

function CashflowTiles({ report }) {
  return (
    <div className="row">
      {cashflowLines(report).map((line) => (
        <div key={line.key} className="col-12 col-md-6 col-xl-4 p-2">
          <div className="card border-0 shadow h-100 p-3">
            <h6 className="text-muted mb-1">{line.label}</h6>
            <h2 className={`mb-1 ${toneClass[line.tone]}`}>{formatMoney(line.value)}</h2>
            <p className="small text-muted mb-0">{line.hint}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Facts({ report }) {
  const facts = [
    { label: 'transacties', value: formatNumber(report.cashflow.transactions) },
    { label: 'actieve kaarten', value: formatNumber(report.cashflow.activeCards) },
    { label: 'verkochte stuks', value: formatNumber(report.cashflow.unitsSold) },
    { label: 'gemiddelde transactie', value: formatMoney(report.cashflow.averageTransaction) },
    { label: 'verbruik per dag', value: formatMoney(report.cashflow.averagePerDay) },
    { label: 'gratis (goudkaarten)', value: formatMoney(report.cashflow.goldSpent) },
  ];

  return (
    <div className="row">
      {facts.map((fact) => (
        <div key={fact.label} className="col-6 col-md-4 col-xl-2 p-2">
          <div className="card border-0 shadow-sm h-100 p-3">
            <h4 className="text-primary mb-0">{fact.value}</h4>
            <p className="small text-muted mb-0">{fact.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function CashflowChart({ report }) {
  const series = buildSeries(report.daily);

  if (!series.points.length) {
    return <div className="alert alert-info mb-0">Geen bewegingen in deze periode.</div>;
  }

  return (
    <Chart
      chartType="ColumnChart"
      data={[
        ['Periode', 'Opgeladen', 'Verbruikt'],
        ...series.points.map((point) => [point.label, point.toppedUp, point.spent]),
      ]}
      options={{
        colors: ['#00BBBD', '#007BB4'],
        legend: { position: 'bottom' },
        chartArea: { width: '88%', height: '72%' },
        vAxis: { format: '€#,##0', gridlines: { color: '#eef2f5' } },
        hAxis: { slantedText: series.points.length > 16, slantedTextAngle: 45 },
        bar: { groupWidth: '70%' },
      }}
      width="100%"
      height="280px"
    />
  );
}

function KingCard({ king }) {
  return (
    <div className="card border-0 shadow text-white p-3 p-md-4 h-100" style={{ backgroundColor: '#E59D23' }}>
      <div className="d-flex align-items-center mb-2">
        <BsTrophyFill className="me-2" />
        <h6 className="mb-0 text-uppercase">Drankkoning</h6>
      </div>
      <h2 className="mb-1">{king.name}</h2>
      <p className="mb-3">
        {king.favourite
          ? `Favoriet: ${king.favourite.name} (${formatNumber(king.favourite.quantity)}x)`
          : 'Geen productlijnen geregistreerd'}
      </p>
      <div className="row text-center mt-auto">
        <div className="col-4">
          <h4 className="mb-0">{formatMoney(king.spent)}</h4>
          <p className="small mb-0">verbruikt</p>
        </div>
        <div className="col-4">
          <h4 className="mb-0">{formatNumber(king.units)}</h4>
          <p className="small mb-0">stuks</p>
        </div>
        <div className="col-4">
          <h4 className="mb-0">{formatNumber(king.visits)}</h4>
          <p className="small mb-0">beurten</p>
        </div>
      </div>
    </div>
  );
}

function Leaderboard({ rows }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? rows : rows.slice(0, 10);

  if (!rows.length) {
    return <div className="alert alert-info mb-0">Niemand heeft in deze periode iets verbruikt.</div>;
  }

  return (
    <>
      <table className="table table-sm table-striped align-middle mb-2">
        <thead>
          <tr>
            <th>#</th>
            <th>Naam</th>
            <th className="d-none d-md-table-cell">Favoriet</th>
            <th className="text-end">Stuks</th>
            <th className="text-end d-none d-sm-table-cell">Beurten</th>
            <th className="text-end">Verbruikt</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => (
            <tr key={row.cardnr}>
              <td className="text-muted">{row.rank}</td>
              <td>{row.name}</td>
              <td className="d-none d-md-table-cell small text-muted">
                {row.favourite ? `${row.favourite.name} (${row.favourite.quantity}x)` : '-'}
              </td>
              <td className="text-end">{formatNumber(row.units)}</td>
              <td className="text-end d-none d-sm-table-cell">{formatNumber(row.visits)}</td>
              <td className="text-end fw-bold">{formatMoney(row.spent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 10 ? (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Toon top 10' : `Toon alle ${rows.length}`}
        </button>
      ) : null}
    </>
  );
}

function ProductTable({ rows }) {
  if (!rows.length) {
    return <div className="alert alert-info mb-0">Niets verkocht in deze periode.</div>;
  }

  return (
    <table className="table table-sm table-striped align-middle mb-0">
      <thead>
        <tr>
          <th>Product</th>
          <th className="d-none d-lg-table-cell">Categorie</th>
          <th className="text-end">Aantal</th>
          <th className="text-end">Omzet</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.name}-${row.category}`}>
            <td>{row.name}</td>
            <td className="d-none d-lg-table-cell small text-muted">{row.category}</td>
            <td className="text-end">{formatNumber(row.quantity)}</td>
            <td className="text-end">{formatMoney(row.revenue)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PurchaseTable({ rows, total }) {
  if (!rows.length) {
    return <div className="alert alert-info mb-0">Geen goedgekeurde bonnen in deze periode.</div>;
  }

  return (
    <table className="table table-sm table-striped align-middle mb-0">
      <thead>
        <tr>
          <th>Leverancier</th>
          <th className="text-end">Bonnen</th>
          <th className="text-end">Bedrag</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.supplier}>
            <td>{row.supplier}</td>
            <td className="text-end">{formatNumber(row.receipts)}</td>
            <td className="text-end">{formatMoney(row.total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th>Totaal</th>
          <th />
          <th className="text-end">{formatMoney(total)}</th>
        </tr>
      </tfoot>
    </table>
  );
}

export default function Statistics() {

  const { walletCode } = useParams();
  const { token } = useCurrentUser();
  const { start, end, setPeriod } = useCurrentPeriod();
  const { getStatistics } = useStatistics();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(!isWalletAdmin(token, walletCode));

  useEffect(() => {
    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        setReport(await getStatistics({ walletCode, start, end }));
      } catch (fetchError) {
        if (fetchError.response?.status === 403) {
          setForbidden(true);
        } else {
          setError({ message: fetchError.response?.data?.error ?? fetchError.message });
        }
      } finally {
        setLoading(false);
      }
    };

    if (!forbidden) {
      fetchStatistics();
    }
  }, [walletCode, start, end, forbidden, getStatistics]);

  const changePeriod = useCallback((range) => setPeriod(range), [setPeriod]);

  if (forbidden) {
    return <Forbidden />;
  }

  return (
    <div className="container my-4">
      <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
        <div>
          <h1 className="mb-1">Statistieken</h1>
          <p className="text-muted mb-0">
            {report ? `${report.wallet.name} · ${formatPeriod(report.period)}` : 'Cijfers voor het bestuur'}
          </p>
        </div>
        <div className="d-flex gap-2 mt-3 mt-md-0">
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={!report}
            onClick={() => downloadStatisticsCsv(report)}
          >
            <BsFileEarmarkSpreadsheet className="me-2" />
            CSV
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!report}
            onClick={() => downloadStatisticsPdf(report)}
          >
            <BsFileEarmarkPdf className="me-2" />
            PDF-rapport
          </button>
        </div>
      </div>

      <PeriodPicker period={{ start, end }} onChange={changePeriod} disabled={loading} />

      <Error error={error} />

      {loading ? (
        <div style={{ height: '40vh' }}>
          <Loader loading={loading} />
        </div>
      ) : null}

      {report && !loading ? (
        <>
          <div className="mt-4">
            <CashflowTiles report={report} />
          </div>

          <div className="mt-2">
            <Facts report={report} />
          </div>

          <div className="row mt-3">
            <div className="col-12 col-xl-8 p-2">
              <div className="card border-0 shadow h-100 p-3">
                <h5>Kasstroom</h5>
                <CashflowChart report={report} />
              </div>
            </div>
            <div className="col-12 col-xl-4 p-2">
              {report.king ? (
                <KingCard king={report.king} />
              ) : (
                <div className="card border-0 shadow h-100 p-3">
                  <h5>Drankkoning</h5>
                  <p className="text-muted mb-0">Nog niemand heeft in deze periode iets verbruikt.</p>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-xl-7 p-2">
              <div className="card border-0 shadow h-100 p-3">
                <h5>Topverbruikers</h5>
                <Leaderboard rows={report.leaderboard} />
              </div>
            </div>
            <div className="col-12 col-xl-5 p-2">
              <div className="card border-0 shadow h-100 p-3">
                <h5>Per categorie</h5>
                {report.categories.length ? (
                  <Chart
                    chartType="PieChart"
                    data={[
                      ['Categorie', 'Aantal'],
                      ...report.categories.map((row) => [row.category, row.quantity]),
                    ]}
                    options={{
                      pieHole: 0.4,
                      legend: { position: 'right' },
                      chartArea: { width: '92%', height: '84%' },
                    }}
                    width="100%"
                    height="260px"
                  />
                ) : (
                  <div className="alert alert-info mb-0">Geen verkopen in deze periode.</div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-xl-7 p-2">
              <div className="card border-0 shadow h-100 p-3">
                <h5>Meest verkocht</h5>
                <ProductTable rows={report.products.slice(0, 15)} />
              </div>
            </div>
            <div className="col-12 col-xl-5 p-2">
              <div className="card border-0 shadow h-100 p-3">
                <h5>Inkoop</h5>
                <PurchaseTable rows={report.purchases} total={report.cashflow.purchases} />
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
